import React, { createContext, useContext, useMemo, useState, ReactNode } from 'react';
import { PricingPeriod, PricingContext as PricingContextType } from '../types/pricing';
import { licensePlans } from '../data/licensePlans';
import { hasPreRenewalGap, planAnnouncedDate } from '../utils/pricingUtils';

const PricingContext = createContext<PricingContextType | undefined>(undefined);

export const PricingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Default to the rate in effect today — the number that governs a new
  // purchase or a renewal, and therefore the one most buyers need.
  const [period, setPeriod] = useState<PricingPeriod>('atRenewal');

  const { announcedDate, hasPreRenewal } = useMemo(() => {
    const now = new Date();
    const announced = licensePlans
      .map(p => planAnnouncedDate(p, now))
      .filter((d): d is string => !!d)
      .sort();
    return {
      announcedDate: announced[0] ?? null,
      hasPreRenewal: licensePlans.some(p => hasPreRenewalGap(p, now)),
    };
  }, []);

  return (
    <PricingContext.Provider value={{ period, setPeriod, announcedDate, hasPreRenewal }}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = (): PricingContextType => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
};
